import { useCallback, useId } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';

import { RichTextEditorField } from '../../../shared/ui/RichTextEditorField';
import { AttachmentsInput } from '../../../shared/ui/AttachmentsInput';
import { DOCUMENT_UPLOAD_ACCEPT } from '../../../shared/data';
import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import { InfoTooltip } from '../../../shared/ui/forms/InfoTooltip';
import { FieldErrorText } from '../../../shared/ui/forms/FieldErrorText';
import { VerticalDivider } from '../../../shared/ui/VerticalDivider';
import type { CriteriaField } from './AuditCriteriaStep';

export interface CriteriaEditorFieldProps {
  readonly field: CriteriaField;
  readonly label: string;
  readonly placeholder: string;
  /** Info-tooltip help text shown next to the label. */
  readonly info: string;
  /** Accessible label for the info-icon trigger. */
  readonly infoAltText: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (field: CriteriaField, value: string) => void;
  /** Files attached to this field via the attachment picker. */
  readonly files: readonly File[];
  /** Emits the full updated file list for this field on attach/remove. */
  readonly onFilesChange: (field: CriteriaField, files: File[]) => void;
  readonly dragAndDropText: string;
  /** Localized inline-error message; when set, the editor shows the invalid
   *  (red-border + aria-invalid) state and the message renders below it. */
  readonly error?: string;
  readonly dataTestId?: string;
}

/**
 * One labelled rich-text criteria field: a required label + info tooltip, the
 * shared {@link RichTextEditorField}, an optional inline error footer, and the
 * field-level attachment control. Extracted from {@link AuditCriteriaStep} so
 * the step stays within the file-size limit and the editor list maps to a
 * single component (keeping the JSX DRY) with a typed onChange.
 *
 * The label stays neutral on error (matching the shared FormField-wrapped
 * fields); only the editor border + the message footer carry the critical red.
 */
export function CriteriaEditorField({
  field,
  label,
  placeholder,
  info,
  infoAltText,
  value,
  disabled,
  onChange,
  files,
  onFilesChange,
  dragAndDropText,
  error,
  dataTestId,
}: CriteriaEditorFieldProps): JSX.Element {
  const errorId = useId();
  const handleChange = useCallback(
    (html: string) => onChange(field, html),
    [field, onChange],
  );
  const handleFilesChange = useCallback(
    (next: File[]) => onFilesChange(field, next),
    [field, onFilesChange],
  );
  const hasError = error !== undefined;
  return (
    <Col gridGap="dimensions.spacing.100">
      {/* Every criteria field is required (WHIZ-002): label + red asterisk,
       *  then an info tooltip carrying the manual-flow help copy. */}
      <Row alignItems="center" gridGap="dimensions.spacing.200">
        <RequiredLabel isRequired hasError={false}>
          {label}
        </RequiredLabel>
        <VerticalDivider />
        <InfoTooltip
          text={info}
          ariaLabel={infoAltText}
          {...(dataTestId !== undefined ? { dataTestId: `${dataTestId}-info` } : {})}
        />
      </Row>
      <RichTextEditorField
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        ariaLabel={label}
        invalid={hasError}
        trackingId={`audit-whiz.review.criteria.${field}`}
        {...(hasError ? { ariaDescribedBy: errorId } : {})}
        {...(dataTestId !== undefined ? { dataTestId } : {})}
      />
      {hasError ? (
        <FieldErrorText
          id={errorId}
          {...(dataTestId !== undefined ? { dataTestId: `${dataTestId}-error` } : {})}
        >
          {error}
        </FieldErrorText>
      ) : null}
      {/* Field-level attachment control for DOCUMENTS only (pdf/doc/sheets/
       *  video). Images are NOT accepted here: they belong inline in the editor
       *  via its image control (embedded as `<img src=mediaId>`, which the
       *  published question-detail view renders), whereas a picker-attached
       *  image lands in mediaIdsMap but the detail view filters images out of
       *  its document list — so it would upload yet never show. Restricting the
       *  picker to documents mirrors the manual (V1) question flow and keeps
       *  every attached file visible after publish. */}
      <AttachmentsInput
        files={[...files]}
        onFilesChange={handleFilesChange}
        dragAndDropText={dragAndDropText}
        accept={DOCUMENT_UPLOAD_ACCEPT}
        disabled={disabled}
        showCapture={false}
      />
    </Col>
  );
}
